import React, { useMemo } from 'react';
import { Row, Col, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { dayFormatType, formatDay } from '~helpers/day';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { formatPriceWithNoSymbol } from '~helpers/currency';
import { useQuery } from '@apollo/client';
import TipsBox from '../tips-box';
import { Spin } from 'antd';
import { QUERY_PROMOTION_PLAN } from '../queries';
import { PAYMENT_PREFERENCE } from '~constants/referral';

const useStyles = makeStyles(
  theme => ({
    tagScheduled: {
      textTransform: 'capitalize',
      background: theme.colors.orange1,
      border: '1px solid',
      borderColor: theme.colors.orange3,
      color: theme.colors.orange6,
    },
    tagActive: {
      textTransform: 'capitalize',
      background: theme.colors.green1,
      border: '1px solid',
      borderColor: theme.colors.green3,
      color: theme.colors.green6,
    },
    tagExpired: {
      textTransform: 'capitalize',
      background: theme.colors.gray2,
      border: '1px solid',
      borderColor: theme.colors.gray5,
      color: 'rgba(0, 0, 0, 0.45)',
    },
  }),
  { name: 'PromotionTags' },
);

function PromotionViewer({ viewPlan }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { data: viewPlanRes } = useQuery(QUERY_PROMOTION_PLAN, {
    variables: {
      id: viewPlan.id,
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const plan = useMemo(() => viewPlanRes?.referralCommissionPlan, [viewPlanRes]);
  const citis = useMemo(() => {
    return plan?.bookingDestinationCities.map(c => c.name) || [];
  }, [plan]);

  if (!plan) {
    return (
      <div className="promotion-plan-viewer__loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <section className="promotion-plan-viewer__section">
        {viewPlan?.partners?.length > 0 && <TipsBox partners={viewPlan?.partners} />}
        <h3 className="promotion-plan-viewer__body__title">
          {t('cms.referral.promotion.modal.basic_info.title')}
        </h3>
        <Row gutter={24}>
          <Col span={12}>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {t('cms.referral.promotion.modal.promotion_name.label')}: {plan.name}
              </span>
              <Tag
                className={classNames('promotion-plan-viewer__body__status-tag', {
                  [classes.tagActive]: plan.status === 'ACTIVE',
                  [classes.tagScheduled]: plan.status === 'SCHEDULED',
                  [classes.tagExpired]: plan.status === 'EXPIRED',
                })}
              >
                {plan.status.toLowerCase() || '-'}
              </Tag>
            </div>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {t('cms.referral.promotion.modal.applied_country.label')}: {plan.country.name}
              </span>
            </div>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {`${t('cms.referral.promotion.modal.long_term.label')} (${plan.currency}): ${
                  plan.tiers[0].longTermCalculationValue
                }`}
              </span>
            </div>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {t('cms.referral.promotion.modal.updated_at.label', {
                  date: formatDay(dayFormatType.UKWITHHM, plan.upadtedAt),
                  name: `${plan.updatedUser.firstName} ${plan.updatedUser.lastName}`,
                })}
              </span>
            </div>
          </Col>

          <Col span={12}>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {t('cms.referral.promotion.modal.external_name.label')}: {plan.externalName}
              </span>
            </div>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {'Partner Payment Preference'}:{' '}
                {plan.paymentPreference ? PAYMENT_PREFERENCE[plan.paymentPreference] : '-'}
              </span>
            </div>

            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {t('cms.referral.promotion.modal.valid_date.label', {
                  value: `${formatDay(dayFormatType.UKFORMAT, plan.validDateFrom)} - ${formatDay(
                    dayFormatType.UKFORMAT,
                    plan.validDateTo,
                  )}`,
                })}
              </span>
            </div>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">{`${t(
                'cms.referral.promotion.modal.short_term.label',
              )} (${plan.currency}): ${formatPriceWithNoSymbol(
                plan.tiers[0].shortTermCalculationValue,
              )}`}</span>
            </div>
          </Col>
        </Row>
      </section>

      <section className="promotion-plan-viewer__section">
        <h3 className="promotion-plan-viewer__body__title">
          {t('cms.referral.promotion.modal.promotion_criteria.title')}
        </h3>
        <Row gutter={24}>
          <Col span={12}>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {t('cms.referral.promotion.modal.destination.label')}:{' '}
                {plan.bookingDestinationCountry.name}
                {citis.length ? ` - ${citis.join(', ')}` : null}
              </span>
            </div>
          </Col>

          <Col span={12}></Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {`${t('cms.referral.promotion.modal.opps_submission_date.label')}: ${formatDay(
                  dayFormatType.UKFORMAT,
                  plan.tiers[0].bookingSubmittedDateFrom,
                )} - ${formatDay(dayFormatType.UKFORMAT, plan.tiers[0].bookingSubmittedDateTo)}`}
              </span>
            </div>
          </Col>

          <Col span={12}>
            <div className="promotion-plan-viewer__body__info-item">
              <span className="promotion-plan-viewer__body__info-item__label">
                {`${t('cms.referral.promotion.modal.opps_completed_date.label')}: ${formatDay(
                  dayFormatType.UKFORMAT,
                  plan.tiers[0].bookingCompletedDateFrom,
                )} - ${formatDay(dayFormatType.UKFORMAT, plan.tiers[0].bookingCompletedDateTo)}`}
              </span>
            </div>
          </Col>
        </Row>
      </section>
    </>
  );
}

export default PromotionViewer;
